import { useEffect, useMemo } from "react";

import {
  ConnectionBaseFragment,
  ReverseEtlSyncFragment,
  SourceType,
} from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  Step,
  StepContextProvider,
  useSteps,
} from "@/components/modules/stepper";
import { useIntegration } from "@/integrations";
import { useMixpanel } from "@/monitoring/mixpanel";
import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";

import {
  StepGroup,
  StepItem,
  StepList,
  StepPanel,
  StepPanels,
} from "../shared/ui/Step";
import {
  ReverseETLSetupProvider,
  useReverseETLSetupContext,
} from "./state/ReverseETLSetupContext";
import { useReverseETLStateContext } from "./state/StepStateContext";
import { ConnectionSettingsStep } from "./steps/connection-settings/ConnectionSettingsStep";
import { DestinationStep } from "./steps/destination/DestinationStep";
import { ModelMappingStep } from "./steps/model-mapping/ModelMappingStep";
import { useValidInputProps } from "./steps/model-mapping/lib/useValidInputProps";
import { SyncSettingsStep } from "./steps/sync-settings/SyncSettingsStep";

export function CreateSyncContainer(props: {
  initialProps: Parameters<typeof useValidInputProps>[0];
  onSetupStarted: (connection: ConnectionBaseFragment) => void;
  onSetupCompleted: (sync: ReverseEtlSyncFragment) => void;
  onSetupError: (error: Error) => void;
}) {
  const mixpanel = useMixpanel();
  const { validInputProps, isLoading } = useValidInputProps(props.initialProps);
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  return (
    <ReverseETLSetupProvider
      integrationId={validInputProps.integrationId}
      connection={validInputProps.connection}
      connectionSettings={validInputProps.connectionSettings}
      initialFormState={
        validInputProps.source
          ? { modelMapping: { source: validInputProps.source } }
          : undefined
      }
      onSetupStarted={(connection) => {
        // A connector has been selected, so now the sync setup will be begin.
        datadogRum.addAction("Reverse ETL Setup Started");
        datadogRum.startSessionReplayRecording();
        mixpanel.track("Reverse ETL Setup Started", {
          integration_id: connection.integrationId,
        });
        mixpanel.time_event("Reverse ETL Setup Completed");
        props.onSetupStarted(connection);
      }}
      onSetupCompleted={(sync) => {
        mixpanel.track("Reverse ETL Setup Completed", {
          source_integration_id: sync.sourceIntegrationId,
          source_type: sync.sourceType,
          source_is_imported_view:
            sync.sourceType === SourceType.RawView &&
            sync.rawView?.syncId == null,
          destination_integration_id: sync.destinationIntegrationId,
          destination_object: sync.primaryObject,
          mapping_count: sync.mappings.length,
        });
        props.onSetupCompleted(sync);
      }}
      onSetupError={(error, state) => {
        mixpanel.track("Reverse ETL Setup Failed", {
          error: error.message,
        });
        Sentry.captureException(error, {
          extra: state,
        });
        props.onSetupError(error);
      }}
    >
      <ReverseETLConfigurationFlow />
    </ReverseETLSetupProvider>
  );
}

function ReverseETLConfigurationFlow() {
  const { state } = useReverseETLStateContext();
  const { connectionHasSettings } = useReverseETLSetupContext();
  const integration = useIntegration(state.destination.value?.integrationId);

  /**
   * TODO
   * - Documentation Panel
   */

  const steps = useMemo(() => {
    const steps: Step[] = [
      {
        id: "destination",
        title: "Destination",
        component: DestinationStep,
      },
      {
        id: "destination-settings",
        title: "Target Details",
        component: ConnectionSettingsStep,
        isDisabled: connectionHasSettings === false,
        tooltip:
          connectionHasSettings === false
            ? `"${integration?.name}" does not have any additional settings to configure`
            : undefined,
      },
      {
        id: "model-mapping",
        title: "Map Data",
        component: ModelMappingStep,
      },
      {
        id: "sync-settings",
        title: "Schedule Sync",
        component: SyncSettingsStep,
      },
    ];
    return steps;
  }, [connectionHasSettings, integration]);

  const stepContext = useSteps(steps, () => {
    if (state.destination.value?.connection) {
      // If initialised with a connection, skip the destination step
      return steps.findIndex((step) => step.id === "destination-settings");
    }
    return 0;
  });

  const { currentStepIndex, setCurrentStepIndex, currentStep, onGoToStep } =
    stepContext;

  useEffect(() => {
    // TODO: this is a bit of a hack:
    // When initialised with a connection, the destination step is skipped
    // where after connection settings are loaded. If there are no settings,
    // we need to skip the connection settings step and go directly to model mapping
    if (
      currentStep.id === "destination-settings" &&
      connectionHasSettings === false
    ) {
      onGoToStep("model-mapping");
    }
  }, [currentStep, connectionHasSettings, onGoToStep]);

  const getStepValidationProps = (stepIndex: number) => {
    const statesArr = Object.values(state);
    const isValid = statesArr[stepIndex]?.isValid;
    // All previous steps must be valid to select this step
    const canSelectStep = statesArr
      .slice(0, stepIndex)
      .every(
        (stepState, index) => steps[index].isDisabled || stepState.isValid,
      );
    return {
      isValid,
      isDisabled: !canSelectStep,
    };
  };

  return (
    <StepContextProvider {...stepContext}>
      <div className="mx-auto flex h-full w-full max-w-4xl flex-col items-center">
        <StepGroup
          selectedIndex={currentStepIndex}
          onChange={setCurrentStepIndex}
        >
          <div className="sticky top-0 z-10 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <StepList
              index={currentStepIndex}
              length={stepContext.steps.length}
            >
              {stepContext.steps.map((step, index) => (
                <StepItem
                  step={step}
                  key={step.id}
                  index={index}
                  {...getStepValidationProps(index)}
                />
              ))}
            </StepList>
          </div>
          <div className="w-full grow py-10">
            <StepPanels>
              {stepContext.steps.map((step, i) => (
                <StepPanel show={i === currentStepIndex} key={step.id}>
                  <div className="w-full px-6 lg:px-12">
                    <step.component />
                  </div>
                </StepPanel>
              ))}
            </StepPanels>
          </div>
        </StepGroup>
      </div>
    </StepContextProvider>
  );
}
